import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Button,
  ListSubheader
} from '@mui/material';
import { AccessTime, Public, ArrowForward, ArrowBack } from '@mui/icons-material';

const TimezoneSelection = ({ selectedTimezone, onTimezoneChange, clinic, onBack }) => {
  // Australian and New Zealand timezones
  const timezones = [
    // Australian timezones
    { value: 'Australia/Sydney', label: 'Sydney (NSW)', offset: '+11:00', country: 'AU' },
    { value: 'Australia/Melbourne', label: 'Melbourne (VIC)', offset: '+11:00', country: 'AU' },
    { value: 'Australia/Brisbane', label: 'Brisbane (QLD)', offset: '+10:00', country: 'AU' },
    { value: 'Australia/Adelaide', label: 'Adelaide (SA)', offset: '+10:30', country: 'AU' },
    { value: 'Australia/Perth', label: 'Perth (WA)', offset: '+8:00', country: 'AU' },
    { value: 'Australia/Hobart', label: 'Hobart (TAS)', offset: '+11:00', country: 'AU' },
    { value: 'Australia/Darwin', label: 'Darwin (NT)', offset: '+9:30', country: 'AU' },
    { value: 'Australia/Canberra', label: 'Canberra (ACT)', offset: '+11:00', country: 'AU' },
    // New Zealand timezones
    { value: 'Pacific/Auckland', label: 'Auckland (NZ)', offset: '+13:00', country: 'NZ' },
    { value: 'Pacific/Chatham', label: 'Chatham Islands (NZ)', offset: '+13:45', country: 'NZ' }
  ];

  // Detect user's browser timezone
  const detectUserTimezone = () => {
    try {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.log('Detected browser timezone:', browserTimezone);
      
      // Check if the detected timezone is in our supported list
      const supportedTimezone = timezones.find(tz => tz.value === browserTimezone);
      if (supportedTimezone) {
        console.log('Browser timezone is supported:', supportedTimezone.label);
        return browserTimezone;
      } else {
        console.log('Browser timezone not in supported list, using default');
        return selectedTimezone;
      }
    } catch (error) {
      console.warn('Failed to detect browser timezone:', error);
      return selectedTimezone;
    }
  };

  const [currentTimezone, setCurrentTimezone] = React.useState(() => {
    // On first load, try to detect user's timezone
    return detectUserTimezone();
  });
  
  const [isAutoDetected, setIsAutoDetected] = React.useState(false);

  // Check if the current timezone was auto-detected
  React.useEffect(() => {
    const detectedTz = detectUserTimezone();
    setIsAutoDetected(detectedTz !== selectedTimezone && detectedTz === currentTimezone);
  }, []);

  const handleTimezoneChange = (event) => {
    const timezone = event.target.value;
    setCurrentTimezone(timezone);
    setIsAutoDetected(false); // User manually changed, so no longer auto-detected
  };

  const handleContinue = () => {
    onTimezoneChange(currentTimezone);
  };

  // Get clinic's timezone for reference
  const clinicTimezone = clinic?.timezone || 'Australia/Sydney';
  const clinicTz = timezones.find(tz => tz.value === clinicTimezone);

  return (
    <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Public sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" component="h2">
          Select Your Timezone
        </Typography>
      </Box>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose your timezone to see appointment times in your local time.
        {clinicTz && (
          <span>
            <br />
            <strong>Clinic timezone:</strong> {clinicTz.label}
          </span>
        )}
        {isAutoDetected && (
          <span>
            <br />
            <strong>✓ Auto-detected:</strong> We've selected your browser's timezone
          </span>
        )}
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="timezone-select-label">Select Your Timezone</InputLabel>
        <Select
          labelId="timezone-select-label"
          id="timezone-select"
          value={currentTimezone}
          label="Select Your Timezone"
          onChange={handleTimezoneChange}
          sx={{ mb: 2 }}
        >
          <ListSubheader>🇦🇺 Australia</ListSubheader>
          {timezones.filter(tz => tz.country === 'AU').map((timezone) => (
            <MenuItem key={timezone.value} value={timezone.value}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {timezone.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  UTC{timezone.offset}
                </Typography>
                {timezone.value === clinicTimezone && (
                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'medium', ml: 1 }}>
                    • Clinic
                  </Typography>
                )}
                {isAutoDetected && timezone.value === currentTimezone && (
                  <Typography variant="caption" color="success.main" sx={{ fontWeight: 'medium', ml: 1 }}>
                    • Auto
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
          
          <ListSubheader>🇳🇿 New Zealand</ListSubheader>
          {timezones.filter(tz => tz.country === 'NZ').map((timezone) => (
            <MenuItem key={timezone.value} value={timezone.value}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {timezone.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  UTC{timezone.offset}
                </Typography>
                {timezone.value === clinicTimezone && (
                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'medium', ml: 1 }}>
                    • Clinic
                  </Typography>
                )}
                {isAutoDetected && timezone.value === currentTimezone && (
                  <Typography variant="caption" color="success.main" sx={{ fontWeight: 'medium', ml: 1 }}>
                    • Auto
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Selected timezone info and continue button */}
      {currentTimezone && (
        <Box sx={{ 
          p: 2, 
          backgroundColor: '#f3f7ff', 
          border: '1px solid #1976d2', 
          borderRadius: 1, 
          mb: 3 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AccessTime sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'medium' }}>
              Selected: {timezones.find(tz => tz.value === currentTimezone)?.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              (UTC{timezones.find(tz => tz.value === currentTimezone)?.offset})
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleContinue}
            endIcon={<ArrowForward />}
            fullWidth
          >
            Continue with {timezones.find(tz => tz.value === currentTimezone)?.label}
          </Button>
        </Box>
      )}

      <Divider sx={{ my: 3 }} />
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">
          All appointment times will be displayed in your selected timezone
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Back button only */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="outlined"
          onClick={onBack}
          startIcon={<ArrowBack />}
          sx={{ minWidth: 120 }}
        >
          Back
        </Button>
      </Box>
    </Paper>
  );
};

export default TimezoneSelection;